// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Click-to-define glossary. `.glossary-link[data-term]` spans anywhere in the UI
 * pop a small definition tooltip; Escape or an outside click dismisses it.
 */

export const GLOSSARY: Record<string, string> = {
  'end-to-end encrypted':
    'Each message is scrambled on the sending device and only unscrambled on the receiving devices. Nothing in between — no relay, no server — can read it.',
  'data channel':
    'A direct WebRTC pipe between two browsers. Messages travel over it device-to-device, encrypted on the wire, with no server in the path.',
  'full mesh':
    'Everyone in the room connects directly to everyone else. There is no central server relaying messages — each message goes straight from your browser to each other browser.',
  webrtc:
    'The browser technology for real-time connections directly between devices — the same tech behind in-browser calling, here used to carry chat messages.',
  dtls:
    'The encryption WebRTC mandates for every peer-to-peer channel. Your messages are encrypted on the wire between the browsers.',
  'aes-gcm':
    'A strong authenticated cipher. Chatwell wraps every message’s bytes in AES-GCM-256 using a key derived from the room passphrase, on top of the channel’s own encryption.',
  'signalling relay':
    'A public service used only to help browsers find each other at the start. It shepherds the encrypted handshake and never carries your messages.',
  stun:
    'A public helper that tells your browser how it appears to the internet, so devices behind home routers can find a direct path to each other.',
  'symmetric nat':
    'A stricter type of home/office router. If two people are both behind one, browsers sometimes can’t find a direct path without a relay — which we deliberately don’t run.',
  sas:
    'Short Authentication String — four words derived from the shared passphrase. If everyone sees the same four words, no impostor slipped into the middle of the room.',
  passphrase:
    'The shared secret for the room. It encrypts the handshake and derives the message key and the four verification words. It lives only in the link’s hash and never reaches any server.',
  'forward secrecy':
    'A property where past messages stay safe even if the key later leaks. Chatwell v1 uses one static key from the passphrase, so it does NOT have forward secrecy — rotate to a fresh room for sensitive conversations.',
  ephemeral:
    'Nothing is stored on a server, ever. Messages exist only in each participant’s tab and are gone when the tab closes or everyone leaves — unless someone chooses to export a transcript.',
};

let tip: HTMLElement | null = null;

export function initGlossary(root: HTMLElement = document.body): void {
  root.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('.glossary-link') as HTMLElement | null;
    if (!target) {
      hide();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const term = target.dataset.term?.toLowerCase() ?? '';
    const def = GLOSSARY[term];
    if (!def) return;
    show(target, term, def);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
  });
  window.addEventListener('scroll', hide, true);
}

function show(anchor: HTMLElement, term: string, def: string): void {
  hide();
  tip = document.createElement('div');
  tip.className = 'glossary-tip';
  tip.innerHTML = `<div class="glossary-term"></div><div class="glossary-def"></div>`;
  (tip.querySelector('.glossary-term') as HTMLElement).textContent = term;
  (tip.querySelector('.glossary-def') as HTMLElement).textContent = def;
  document.body.appendChild(tip);
  const r = anchor.getBoundingClientRect();
  const tr = tip.getBoundingClientRect();
  let left = r.left;
  if (left + tr.width > window.innerWidth - 12) left = window.innerWidth - tr.width - 12;
  if (left < 12) left = 12;
  let top = r.bottom + 8;
  if (top + tr.height > window.innerHeight - 12) top = r.top - tr.height - 8;
  tip.style.left = `${left}px`;
  tip.style.top = `${Math.max(12, top)}px`;
}

function hide(): void {
  if (tip) {
    tip.remove();
    tip = null;
  }
}
